import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";

function PaymentSummary() {
    const { periodId } = useParams();
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notification, setNotification] = useState({ type: "", message: "" });
    const token = localStorage.getItem("access_token");
    const navigate = useNavigate();

    const fetchPaymentSummary = async () => {
        try {
            const response = await fetch(`${API_URL}/payment-periods/${periodId}/summary`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setSummaryData(data.data);
                
                if (data.data.period_status === 'Closed') {
                    setNotification({
                        type: "danger",
                        message: "These payments cannot be processed as the period is closed"
                    });
                }
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch payment summary");
            }
        } catch (error) {
            setError("An error occurred while fetching payment summary.");
            setNotification({
                type: "danger",
                message: "Failed to load payment summary data"
            });
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPaymentSummary();
    }, [periodId]);

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    return (
        <div className="container">
            <main className="box">
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <h1 className="title is-4">
                                {summaryData?.period_name}
                                &nbsp;
                                {summaryData?.period_status === 'Closed' && (
                                    <span className="tag is-danger"> {summaryData?.period_status} </span>
                                )}
                                {summaryData?.period_status === 'Open' && (
                                    <span className="tag is-success"> {summaryData?.period_status} </span>
                                )}
                            </h1>
                        </div>
                    </div>
                    <div className="level-right">
                        <div className="level-item">
                            <Link
                                className="button is-info"
                                to="/dashboard/payment-periods"
                            >
                                <span className="icon">
                                    <i className="fa-solid fa-arrow-left"></i>
                                </span>
                                <span>Back</span>
                            </Link>
                        </div>
                    </div>
                </nav>

                {summaryData?.period_status === 'Closed' && (
                    <Notification
                        type="danger"
                        message="These payments cannot be processed as the period is closed"
                        isVisible={true}
                    />
                )}

                {notification.message && notification.type !== 'danger' && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {loading ? (
                    <div className="has-text-centered">
                        <p>Loading payment summary...</p>
                        <progress className="progress is-small is-primary" max="100">Loading</progress>
                    </div>
                ) : error ? (
                    <div className="notification is-danger">
                        {error}
                    </div>
                ) : summaryData ? (
                    <div>
                        <div className="box has-background-light">
                            <div className="level">
                                <div className="level-left">
                                    <div className="level-item">
                                        <h2 className="subtitle is-5">
                                            Total Amount: <strong>{summaryData.total_amount}</strong>
                                        </h2>
                                    </div>
                                </div>
                                <div className="level-right">
                                    <div className="level-item">
                                        <Link
                                            to={`/dashboard/payment-periods/${periodId}/payments`}
                                            className="button is-primary is-small"
                                            disabled={summaryData.period_status === 'Closed'}
                                        >
                                            <span className="icon">
                                                <i className="fas fa-list"></i>
                                            </span>
                                            <span>View All Payments</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="table-container">
                            <table className="table is-striped is-fullwidth is-hoverable">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Route Name</th>
                                        <th>Total Amount</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryData.routes.length > 0 ? (
                                        summaryData.routes.map((route, index) => (
                                            <tr key={index}>
                                                <td>{index+1}</td>
                                                <td>{route.route_name}</td>
                                                <td>{route.total_amount}</td>
                                                <td>
                                                    <Link
                                                        to={`/dashboard/payment-periods/${periodId}/route/${route.route_id}`}
                                                        className="button is-small is-info"
                                                        disabled={summaryData.period_status === 'Closed'}
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-eye"></i>
                                                        </span>
                                                        <span>View Route Payments</span>
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="has-text-centered">
                                                No payment data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : null}
            </main>
        </div>
    );
}

export default PaymentSummary;